"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.scrollContainerBottomPadding = void 0;
exports.ScrollableParent = ScrollableParent;
exports.useScrollableParent = useScrollableParent;
const jsx_runtime_1 = require("@emotion/react/jsx-runtime");
const react_1 = require("react");
const Css_1 = require("../../Css");
const ScrollableParentContext = (0, react_1.createContext)({
    scrollableEl: null,
    pr: 0,
    pl: 0,
    setPortalTick: (v) => { },
});
/**
 * Provides a pattern for implementing "multiple sticky" components.
 *
 * In css, `position: sticky` is great for pinning 1 element to the top of a container.
 *
 * However, in UX patterns, we're often asked to pin multiple DOM elements that are actually
 * spread across multiple React components. For example:
 *
 * - Sticky a Header (in FooPage)
 * - Sticky the table filter & actions (in FooTable)
 * - Sticky the table header row(s) (in GridTable)
 *
 * Historically the way we did this was passing `stickyOffset`s around, where the header would be
 * `top: 0px`, the filter & actions would be `top: ${headerPx}px`, and the table header rows would
 * be `top: ${headerPx + filterActionsPx}px`.
 *
 * However, this is brittle as the `headerPx` / `filterActionsPx` are likely dynamic.
 *
 * `ScrollableParent` solves this by putting all the stickied content (except the table header rows)
 * into a single div, and then having the page use `ScrollableContent` to mark what should actually
 * scroll, which then we "pull up" to be a sibling div of "everything that was stickied".
 *
 * See [this miro](https://miro.com/app/board/o9J_l-FQ-RU=/) and how we need to "cut the component in half".
 */
function ScrollableParent(props) {
    const { children, xss, tagName: Tag = "div" } = props;
    const scrollableEl = (0, react_1.useMemo)(() => {
        const el = document.createElement("div");
        // Ensure this wrapping div takes up the full height of its container
        el.style.height = "100%";
        return el;
    }, []);
    const [, setTick] = (0, react_1.useState)(0);
    const hasScrollableContent = scrollableEl.childNodes.length > 0;
    const scrollableRef = (0, react_1.useRef)(null);
    const { paddingLeft, paddingRight, ...otherXss } = xss || {};
    const context = {
        scrollableEl,
        pl: paddingLeft !== null && paddingLeft !== void 0 ? paddingLeft : 0,
        pr: paddingRight !== null && paddingRight !== void 0 ? paddingRight : 0,
        setPortalTick: setTick,
    };
    (0, react_1.useEffect)(() => {
        scrollableRef.current.appendChild(scrollableEl);
    }, [scrollableEl]);
    return ((0, jsx_runtime_1.jsx)(ScrollableParentContext.Provider, { value: context, children: (0, jsx_runtime_1.jsxs)(Tag, { css: { ...Css_1.Css.mh0.mw0.fg1.df.fdc.$, ...otherXss }, children: [(0, jsx_runtime_1.jsx)("div", { css: {
                        ...Css_1.Css.pl(context.pl).pr(context.pr).$,
                        ...(!hasScrollableContent ? { ...Css_1.Css.oa.h100.$, ...exports.scrollContainerBottomPadding } : undefined),
                    }, children: children }), (0, jsx_runtime_1.jsx)("div", { css: Css_1.Css.fg1.oa.$, ref: scrollableRef })] }) }));
}
function useScrollableParent() {
    return (0, react_1.useContext)(ScrollableParentContext);
}
// Styles to wrap around the scrollable content in order to give padding beneath the content within the scrollable container.
exports.scrollContainerBottomPadding = Css_1.Css.addIn("&:after", Css_1.Css.contentEmpty.db.h2.$).$;
